import type { InteriorResult } from './protocol';

/**
 * Reassemble a floor generated in parts (InteriorParams.part): the mesh comes from part 0, each room's
 * objects and debug from the part that decorated it (room i from part i mod n), and the furniture
 * buffers are rebuilt in room order, so the result equals the unsplit job's.
 */
export function mergeInteriorParts(parts: InteriorResult[]): InteriorResult {
  const n = parts.length;
  const byPart = new Array<InteriorResult>(n);
  for (const p of parts) byPart[p.part[0]] = p;
  const base = byPart[0];
  const cursor = new Array<number>(n).fill(0);
  const count = base.rooms.reduce((a, _, i) => a + byPart[i % n].rooms[i].objects.length, 0);
  const matrices = new Float32Array(count * 16);
  const colors = new Float32Array(count * 3);
  const kinds = new Uint16Array(count);
  let at = 0;
  const rooms = base.rooms.map((_, i) => {
    const src = byPart[i % n];
    const k = src.rooms[i].objects.length;
    const c = cursor[i % n];
    matrices.set(src.furniture.matrices.subarray(c * 16, (c + k) * 16), at * 16);
    colors.set(src.furniture.colors.subarray(c * 3, (c + k) * 3), at * 3);
    kinds.set(src.furniture.kinds.subarray(c, c + k), at);
    cursor[i % n] += k;
    at += k;
    // Part 0 built the mesh, so it alone knows each room's vertex range.
    return { ...src.rooms[i], vertexRange: base.rooms[i].vertexRange };
  });
  return { ...base, rooms, furniture: { matrices, colors, kinds }, part: [0, 1], ms: Math.max(...parts.map((p) => p.ms)) };
}
